/**
 * Feedback Tracker — ค่าคงที่กลาง (สถานะ / priority / ประเภท / event) + ตัวจัดรูปวันเวลา
 *
 * mirror `docs/FEEDBACK_SYSTEM_DESIGN.md` — status เป็น allow-list (append ท้ายเท่านั้น)
 * ทุกหน้า/ป้าย/ตัวกรองต้องอ้างจากไฟล์นี้ ห้ามเขียนรายการสถานะซ้ำ
 */
import { toIctInstant } from './dateUtils';

export type FeedbackStatus =
  | 'NEW'
  | 'INVESTIGATING'
  | 'IN_PROGRESS'
  | 'FIXED_PENDING_DEPLOY'
  | 'DONE'
  | 'WONT_FIX'
  | 'DUPLICATE';

export type FeedbackPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
export type FeedbackCategory = 'bug' | 'feedback' | 'suggestion';

export type FeedbackEventType =
  | 'CREATED'
  | 'EDITED'
  | 'STATUS_CHANGED'
  | 'COMMENT'
  | 'ATTACHMENT_ADDED'
  | 'ATTACHMENT_REMOVED'
  | 'ASSIGNED'
  | 'PRIORITY_CHANGED';

/** สถานะที่ยังไม่ปิด — ตรงกับ OPEN_FEEDBACK_STATUSES ฝั่ง backend */
export const OPEN_FEEDBACK_STATUSES: ReadonlyArray<FeedbackStatus> = [
  'NEW',
  'INVESTIGATING',
  'IN_PROGRESS',
  'FIXED_PENDING_DEPLOY',
];
/** สถานะที่ปิดแล้ว */
export const CLOSED_FEEDBACK_STATUSES: ReadonlyArray<FeedbackStatus> = ['DONE', 'WONT_FIX', 'DUPLICATE'];

/** สถานะที่บังคับ "หมายเหตุการแก้ไข" */
/** สถานะที่ "มีผลการแก้" — ตรงกับ RESOLUTION_FEEDBACK_STATUSES ฝั่ง backend · หมายเหตุการแก้ไข/อ้างอิงการแก้มีความหมายเฉพาะสถานะเหล่านี้ */
export const RESOLUTION_FEEDBACK_STATUSES: ReadonlyArray<FeedbackStatus> = ['FIXED_PENDING_DEPLOY', 'DONE', 'WONT_FIX', 'DUPLICATE'];

export const NOTE_REQUIRED_STATUSES: ReadonlyArray<FeedbackStatus> = ['WONT_FIX', 'DUPLICATE'];

export const FEEDBACK_STATUS_META: ReadonlyArray<{ value: FeedbackStatus; label: string; badge: string }> = [
  { value: 'NEW', label: 'ใหม่', badge: 'bg-primary-50 text-primary-700 border-primary-200' },
  { value: 'INVESTIGATING', label: 'กำลังตรวจสอบ', badge: 'bg-warning-50 text-warning-700 border-warning-200' },
  { value: 'IN_PROGRESS', label: 'กำลังแก้ไข', badge: 'bg-warning-100 text-warning-800 border-warning-300' },
  { value: 'FIXED_PENDING_DEPLOY', label: 'แก้แล้ว รอ deploy', badge: 'bg-info-50 text-info-700 border-info-200' },
  { value: 'DONE', label: 'แก้แล้ว (ขึ้นระบบจริง)', badge: 'bg-success-50 text-success-700 border-success-200' },
  { value: 'WONT_FIX', label: 'ไม่แก้', badge: 'bg-secondary-100 text-secondary-700 border-secondary-300' },
  { value: 'DUPLICATE', label: 'ซ้ำกับเรื่องอื่น', badge: 'bg-secondary-100 text-secondary-600 border-secondary-300' },
];

export const FEEDBACK_PRIORITY_META: ReadonlyArray<{ value: FeedbackPriority; label: string; badge: string }> = [
  { value: 'CRITICAL', label: 'วิกฤต', badge: 'bg-danger-50 text-danger-700 border-danger-200' },
  { value: 'HIGH', label: 'สูง', badge: 'bg-warning-50 text-warning-700 border-warning-200' },
  { value: 'MEDIUM', label: 'ปานกลาง', badge: 'bg-primary-50 text-primary-700 border-primary-200' },
  { value: 'LOW', label: 'ต่ำ', badge: 'bg-secondary-100 text-secondary-600 border-secondary-300' },
];

export const FEEDBACK_CATEGORY_META: ReadonlyArray<{ value: FeedbackCategory; label: string; badge: string }> = [
  { value: 'bug', label: 'แจ้งบั๊ก', badge: 'bg-danger-50 text-danger-700 border-danger-200' },
  { value: 'feedback', label: 'Feedback', badge: 'bg-primary-50 text-primary-700 border-primary-200' },
  { value: 'suggestion', label: 'ข้อเสนอแนะ', badge: 'bg-accent-50 text-accent-700 border-accent-200' },
];

const NEUTRAL_BADGE = 'bg-secondary-100 text-secondary-600 border-secondary-300';

const find = <T extends { value: string }>(list: ReadonlyArray<T>, v: string | null | undefined): T | undefined =>
  list.find((m) => m.value === v);

export const feedbackStatusLabel = (v: string | null | undefined): string =>
  find(FEEDBACK_STATUS_META, v)?.label ?? (v || '-');
export const feedbackStatusBadge = (v: string | null | undefined): string =>
  find(FEEDBACK_STATUS_META, v)?.badge ?? NEUTRAL_BADGE;
export const feedbackPriorityLabel = (v: string | null | undefined): string =>
  v ? find(FEEDBACK_PRIORITY_META, v)?.label ?? v : 'ยังไม่กำหนด';
export const feedbackPriorityBadge = (v: string | null | undefined): string =>
  find(FEEDBACK_PRIORITY_META, v)?.badge ?? NEUTRAL_BADGE;
export const feedbackCategoryLabel = (v: string | null | undefined): string =>
  find(FEEDBACK_CATEGORY_META, v)?.label ?? (v || '-');
export const feedbackCategoryBadge = (v: string | null | undefined): string =>
  find(FEEDBACK_CATEGORY_META, v)?.badge ?? NEUTRAL_BADGE;

export const FEEDBACK_SORT_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '-created_at', label: 'แจ้งล่าสุดก่อน' },
  { value: 'created_at', label: 'แจ้งเก่าสุดก่อน' },
  { value: 'priority', label: 'ความสำคัญ (วิกฤตก่อน)' },
  { value: '-updated_at', label: 'อัปเดตล่าสุดก่อน' },
  { value: 'updated_at', label: 'อัปเดตเก่าสุดก่อน' },
  { value: 'status', label: 'สถานะ' },
];
export const DEFAULT_FEEDBACK_SORT = '-created_at';
export const MEETING_SORT = 'priority';

/** วว/ดด/ปปปป ชม:นน (พ.ศ. · เวลาไทย) — ข้อมูลจาก backend เป็นเวลาไทย naive */
export function formatFeedbackDateTime(value: string | null | undefined): string {
  if (!value) return '-';
  const d = new Date(toIctInstant(value));
  if (isNaN(d.getTime())) return '-';
  const parts = new Intl.DateTimeFormat('th-TH-u-ca-buddhist', {
    timeZone: 'Asia/Bangkok',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(d);
  const g = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${g('day')}/${g('month')}/${g('year')} ${g('hour')}:${g('minute')}`;
}

/** ตัดสินใจจากข้อเท็จจริงของ event — คืนข้อความไทยหนึ่งบรรทัดของเหตุการณ์ (ไม่รวม note/diff) */
export function describeFeedbackEvent(e: {
  event_type: string;
  field?: string | null;
  from_value: string | null;
  to_value: string | null;
}): string {
  switch (e.event_type) {
    case 'CREATED':
      return 'แจ้งเรื่องนี้';
    case 'EDITED':
      if (e.field === 'category') return `เปลี่ยนประเภท ${feedbackCategoryLabel(e.from_value)} → ${feedbackCategoryLabel(e.to_value)}`;
      if (e.field === 'fixed_ref') return `แก้อ้างอิงการแก้ ${e.from_value || '-'} → ${e.to_value || '-'}`;
      return 'แก้ไขรายละเอียด';
    case 'STATUS_CHANGED':
      return `เปลี่ยนสถานะ ${feedbackStatusLabel(e.from_value)} → ${feedbackStatusLabel(e.to_value)}`;
    case 'COMMENT':
      return 'แสดงความคิดเห็น';
    case 'ATTACHMENT_ADDED':
      return `เพิ่มรูปแนบ${e.to_value ? ` ${e.to_value}` : ''}`;
    case 'ATTACHMENT_REMOVED':
      return `ลบรูปแนบ${e.from_value ? ` ${e.from_value}` : ''}`;
    case 'ASSIGNED':
      return e.to_value
        ? `มอบหมายให้ ${e.to_value}${e.from_value ? ` (จาก ${e.from_value})` : ''}`
        : `ยกเลิกการมอบหมาย${e.from_value ? ` (เดิม ${e.from_value})` : ''}`;
    case 'PRIORITY_CHANGED':
      return `เปลี่ยนความสำคัญ ${feedbackPriorityLabel(e.from_value)} → ${feedbackPriorityLabel(e.to_value)}`;
    default:
      return e.event_type;
  }
}
